import React, { useEffect, useState } from 'react';
import { confirmAction } from '../../utils/confirm';
import {
    ZigbeeDefaultSetting, ZigbeeDefaultSettingInput, DefaultOperation,
    fetchZigbeeDefaultSettings, createZigbeeDefaultSetting,
    updateZigbeeDefaultSetting, deleteZigbeeDefaultSetting,
} from '../../services/zigbeeDefaultSettingsApi';

const EMPTY_FORM: ZigbeeDefaultSettingInput = {
    manufacturer_name: '', model_id: '',
    operation: 'reporting', cluster: '', attribute: '',
    endpoint: null, min_interval: 10, max_interval: 600, reportable_change: 50,
    write_value: null, enabled: true, description: '',
};

const numOrNull = (v: string): number | null => (v.trim() === '' ? null : Number(v));

const ZigbeeDefaultSettingsPanel: React.FC = () => {
    const [open, setOpen] = useState(false);
    const [rows, setRows] = useState<ZigbeeDefaultSetting[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [showAdd, setShowAdd] = useState(false);
    const [addForm, setAddForm] = useState<ZigbeeDefaultSettingInput>(EMPTY_FORM);
    const [addSaving, setAddSaving] = useState(false);

    const [editingId, setEditingId] = useState<number | null>(null);
    const [editForm, setEditForm] = useState<ZigbeeDefaultSettingInput>(EMPTY_FORM);
    const [editSaving, setEditSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            setRows(await fetchZigbeeDefaultSettings());
            setError(null);
        } catch (e: any) {
            setError(e.message || 'Kunde inte hämta standardinställningarna');
        } finally { setLoading(false); }
    };

    useEffect(() => { if (open) load(); }, [open]);

    const validate = (f: ZigbeeDefaultSettingInput): string | null => {
        if (!f.cluster.trim()) return 'Kluster måste fyllas i';
        if (!f.attribute.trim()) return 'Egenskap måste fyllas i';
        if (f.operation === 'reporting' && (f.min_interval === null || f.max_interval === null)) {
            return 'Rader för rapportering behöver både kortast och längst tid';
        }
        if (f.operation === 'write' && f.write_value === null) return 'Rader som skriver ett värde behöver ett värde';
        return null;
    };

    const handleAdd = async () => {
        const v = validate(addForm);
        if (v) { setError(v); return; }
        setAddSaving(true);
        try {
            await createZigbeeDefaultSetting({ ...addForm, cluster: addForm.cluster.trim(), attribute: addForm.attribute.trim() });
            setAddForm(EMPTY_FORM); setShowAdd(false);
            await load();
        } catch (e: any) { setError(e.message || 'Kunde inte skapa raden'); }
        finally { setAddSaving(false); }
    };

    const startEdit = (r: ZigbeeDefaultSetting) => {
        setEditingId(r.id);
        const { id, ...rest } = r;
        setEditForm(rest);
    };

    const handleEditSave = async () => {
        if (editingId === null) return;
        const v = validate(editForm);
        if (v) { setError(v); return; }
        setEditSaving(true);
        try {
            await updateZigbeeDefaultSetting(editingId, { ...editForm, cluster: editForm.cluster.trim(), attribute: editForm.attribute.trim() });
            setEditingId(null);
            await load();
        } catch (e: any) { setError(e.message || 'Kunde inte spara raden'); }
        finally { setEditSaving(false); }
    };

    const handleToggleEnabled = async (r: ZigbeeDefaultSetting) => {
        try {
            await updateZigbeeDefaultSetting(r.id, { ...r, enabled: !r.enabled });
            await load();
        } catch (e: any) { setError(e.message || 'Kunde inte spara raden'); }
    };

    const handleDelete = async (r: ZigbeeDefaultSetting) => {
        if (!confirmAction(`Ta bort standardinställningen ${r.cluster}.${r.attribute}?`)) return;
        try {
            await deleteZigbeeDefaultSetting(r.id);
            await load();
        } catch (e: any) { setError(e.message || 'Kunde inte ta bort raden'); }
    };

    const inp: React.CSSProperties = { padding: '5px 8px', fontSize: '13px', border: '1px solid #ccc', borderRadius: '4px' };
    const lbl: React.CSSProperties = { fontSize: '11px', fontWeight: 'bold', display: 'block', marginBottom: '3px' };

    const FormRow: React.FC<{ form: ZigbeeDefaultSettingInput; setForm: (fn: (f: ZigbeeDefaultSettingInput) => ZigbeeDefaultSettingInput) => void }> = ({ form, setForm }) => (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px', alignItems: 'end' }}>
            <div>
                <label style={lbl}>Tillverkare <span style={{ color: '#888', fontWeight: 'normal' }}>(tomt = alla, * / ? går bra)</span></label>
                <input style={{ ...inp, width: '100%' }} value={form.manufacturer_name} placeholder="(alla) eller _TZ3000_*"
                    onChange={e => setForm(f => ({ ...f, manufacturer_name: e.target.value }))} />
            </div>
            <div>
                <label style={lbl}>Modell-ID <span style={{ color: '#888', fontWeight: 'normal' }}>(tomt = alla, * / ? går bra)</span></label>
                <input style={{ ...inp, width: '100%' }} value={form.model_id} placeholder="(alla) eller TS0*"
                    onChange={e => setForm(f => ({ ...f, model_id: e.target.value }))} />
            </div>
            <div>
                <label style={lbl}>Gör så här</label>
                <select style={{ ...inp, width: '100%' }} value={form.operation}
                    onChange={e => setForm(f => ({ ...f, operation: e.target.value as DefaultOperation }))}>
                    <option value="reporting">rapportera</option>
                    <option value="write">skriv värde</option>
                </select>
            </div>
            <div>
                <label style={lbl}>Kluster</label>
                <input style={{ ...inp, width: '100%' }} value={form.cluster} placeholder="genPollCtrl"
                    onChange={e => setForm(f => ({ ...f, cluster: e.target.value }))} />
            </div>
            <div>
                <label style={lbl}>Egenskap</label>
                <input style={{ ...inp, width: '100%' }} value={form.attribute} placeholder="checkinInterval"
                    onChange={e => setForm(f => ({ ...f, attribute: e.target.value }))} />
            </div>
            <div>
                <label style={lbl}>Slutpunkt <span style={{ color: '#888', fontWeight: 'normal' }}>(tomt = alla)</span></label>
                <input type="number" style={{ ...inp, width: '100%' }} value={form.endpoint ?? ''} placeholder="alla"
                    onChange={e => setForm(f => ({ ...f, endpoint: numOrNull(e.target.value) }))} />
            </div>

            {form.operation === 'reporting' ? (
                <>
                    <div>
                        <label style={lbl}>Kortast tid (s)</label>
                        <input type="number" style={{ ...inp, width: '100%' }} value={form.min_interval ?? ''}
                            onChange={e => setForm(f => ({ ...f, min_interval: numOrNull(e.target.value) }))} />
                    </div>
                    <div>
                        <label style={lbl}>Längst tid (s)</label>
                        <input type="number" style={{ ...inp, width: '100%' }} value={form.max_interval ?? ''}
                            onChange={e => setForm(f => ({ ...f, max_interval: numOrNull(e.target.value) }))} />
                    </div>
                    <div>
                        <label style={lbl}>Minsta ändring att rapportera</label>
                        <input type="number" style={{ ...inp, width: '100%' }} value={form.reportable_change ?? ''}
                            onChange={e => setForm(f => ({ ...f, reportable_change: numOrNull(e.target.value) }))} />
                    </div>
                </>
            ) : (
                <div>
                    <label style={lbl}>Värde att skriva</label>
                    <input type="number" style={{ ...inp, width: '100%' }} value={form.write_value ?? ''}
                        onChange={e => setForm(f => ({ ...f, write_value: numOrNull(e.target.value) }))} />
                </div>
            )}

            <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 'bold', cursor: 'pointer', paddingTop: '16px' }}>
                    <input type="checkbox" checked={form.enabled}
                        onChange={e => setForm(f => ({ ...f, enabled: e.target.checked }))} />
                    Påslagen
                </label>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
                <label style={lbl}>Beskrivning</label>
                <input style={{ ...inp, width: '100%' }} value={form.description ?? ''}
                    onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
            </div>
        </div>
    );

    const formatValue = (r: ZigbeeDefaultSetting): string =>
        r.operation === 'reporting'
            ? `minst ${r.min_interval} s, högst ${r.max_interval} s, Δ ${r.reportable_change}`
            : `värde ${r.write_value}`;

    return (
        <div style={{ marginBottom: '20px', border: '1px solid #dee2e6', borderRadius: '6px', overflow: 'hidden' }}>
            <button type="button" onClick={() => setOpen(o => !o)}
                style={{ width: '100%', background: '#e8f4fd', border: 'none', padding: '10px 14px', cursor: 'pointer', textAlign: 'left', fontWeight: 'bold', fontSize: '13px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>📡 Standardinställningar för Zigbee (skickas vid parkoppling)</span>
                <span>{open ? '▲' : '▼'}</span>
            </button>
            {open && (
                <div style={{ padding: '14px 16px', background: '#f8fcff' }}>
                    <div style={{ fontSize: '12px', color: '#555', marginBottom: '10px' }}>
                        De här inställningarna skickas när en enhet parkopplas. Tillverkare och modell kan lämnas tomma (gäller alla),
                        skrivas exakt, eller innehålla <code>*</code>/<code>?</code> (t.ex. <code>_TZ3000_*</code>, <code>TS0*</code>).
                        Den mest exakta raden vinner: exakt före jokertecken före tomt, och tillverkare går före modell.
                        En inställning på enskild enhet vinner alltid över de här.
                    </div>
                    {error && (
                        <div style={{ background: '#f8d7da', border: '1px solid #f5c6cb', color: '#721c24', padding: '8px 12px', borderRadius: '4px', marginBottom: '10px', fontSize: '13px' }}>
                            {error} <button onClick={() => setError(null)} style={{ marginLeft: '8px', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 'bold' }}>✕</button>
                        </div>
                    )}

                    {loading ? (
                        <div style={{ color: '#888', fontSize: '13px' }}>Hämtar…</div>
                    ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                            <thead>
                                <tr style={{ background: '#e9ecef' }}>
                                    {['Tillverkare', 'Modell', 'Gör', 'Kluster', 'Egenskap', 'Slutp.', 'Värde', 'På', ''].map(h => (
                                        <th key={h} style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 'bold', borderBottom: '1px solid #dee2e6' }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map(r => editingId === r.id ? (
                                    <tr key={r.id} style={{ background: '#fff9e6' }}>
                                        <td colSpan={9} style={{ padding: '10px' }}>
                                            <FormRow form={editForm} setForm={setEditForm as any} />
                                            <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                                                <button onClick={handleEditSave} disabled={editSaving}
                                                    style={{ padding: '5px 14px', fontSize: '13px', background: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>
                                                    {editSaving ? 'Sparar…' : 'Spara'}
                                                </button>
                                                <button onClick={() => setEditingId(null)}
                                                    style={{ padding: '5px 14px', fontSize: '13px', background: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                                                    Avbryt
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    <tr key={r.id} style={{ borderBottom: '1px solid #f0f0f0', opacity: r.enabled ? 1 : 0.5 }}>
                                        <td style={{ padding: '6px 10px', fontFamily: 'monospace' }}>{r.manufacturer_name || <span style={{ color: '#aaa' }}>alla</span>}</td>
                                        <td style={{ padding: '6px 10px', fontFamily: 'monospace' }}>{r.model_id || <span style={{ color: '#aaa' }}>alla</span>}</td>
                                        <td style={{ padding: '6px 10px' }}>{r.operation === 'reporting' ? 'rapportera' : 'skriv'}</td>
                                        <td style={{ padding: '6px 10px', fontFamily: 'monospace' }}>{r.cluster}</td>
                                        <td style={{ padding: '6px 10px', fontFamily: 'monospace' }}>{r.attribute}</td>
                                        <td style={{ padding: '6px 10px', textAlign: 'center' }}>{r.endpoint ?? <span style={{ color: '#aaa' }}>alla</span>}</td>
                                        <td style={{ padding: '6px 10px', fontFamily: 'monospace', fontSize: '11px', color: '#555' }}>{formatValue(r)}</td>
                                        <td style={{ padding: '6px 10px', textAlign: 'center' }}>
                                            <input type="checkbox" checked={r.enabled} onChange={() => handleToggleEnabled(r)} title="Påslagen" />
                                        </td>
                                        <td style={{ padding: '6px 10px', whiteSpace: 'nowrap' }}>
                                            <button onClick={() => startEdit(r)}
                                                style={{ padding: '3px 10px', fontSize: '12px', marginRight: '6px', background: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                                                Ändra
                                            </button>
                                            <button onClick={() => handleDelete(r)}
                                                style={{ padding: '3px 10px', fontSize: '12px', background: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                                                Ta bort
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {rows.length === 0 && (
                                    <tr><td colSpan={9} style={{ padding: '10px', color: '#888' }}>Inga standardinställningar än.</td></tr>
                                )}
                            </tbody>
                        </table>
                    )}

                    {showAdd ? (
                        <div style={{ marginTop: '14px', padding: '12px', background: '#fff', border: '1px solid #dee2e6', borderRadius: '4px' }}>
                            <div style={{ fontWeight: 'bold', fontSize: '13px', marginBottom: '10px' }}>Ny standardinställning</div>
                            <FormRow form={addForm} setForm={setAddForm as any} />
                            <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                                <button onClick={handleAdd} disabled={addSaving}
                                    style={{ padding: '5px 14px', fontSize: '13px', background: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>
                                    {addSaving ? 'Sparar…' : 'Lägg till'}
                                </button>
                                <button onClick={() => { setShowAdd(false); setAddForm(EMPTY_FORM); }}
                                    style={{ padding: '5px 14px', fontSize: '13px', background: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                                    Avbryt
                                </button>
                            </div>
                        </div>
                    ) : (
                        <button onClick={() => setShowAdd(true)}
                            style={{ marginTop: '12px', padding: '6px 14px', fontSize: '13px', background: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>
                            + Lägg till standardinställning
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default ZigbeeDefaultSettingsPanel;
